import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { StatCard } from "@/components/reports/stat-card";
import { BarChart } from "@/components/reports/bar-chart";

export const Route = createFileRoute("/_authenticated/reports/")({
  component: ReportsOverview,
});

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

interface CompletionRow {
  projectId: string;
  projectName: string;
  total: number;
  approved: number;
  submitted: number;
  draft: number;
  rejected: number;
}

interface ReviewLagRow {
  flhaId: string;
  projectName: string;
  creatorName: string;
  submittedAt: string;
  hoursWaiting: number;
}

interface PermitComplianceData {
  byStatus: {
    draft: number;
    active: number;
    completed: number;
    cancelled: number;
    expired: number;
  };
  closeOutRate: number;
  total: number;
}

interface HazardRow {
  hazardLabel: string;
  category: string;
  count: number;
}

interface HighRiskRow {
  highRiskType: string;
  projectName: string;
  count: number;
}

function useReportSearch() {
  // Access search params from the parent reports layout
  const params = new URLSearchParams(window.location.search);
  return {
    dateFrom: params.get("dateFrom") || daysAgo(30),
    dateTo: params.get("dateTo") || today(),
    projectId: params.get("projectId") || undefined,
  };
}

function buildQueryString(params: {
  dateFrom: string;
  dateTo: string;
  projectId?: string;
}) {
  const qs = new URLSearchParams({
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
  });
  if (params.projectId) qs.set("projectId", params.projectId);
  return qs.toString();
}

function ReportsOverview() {
  const search = useReportSearch();
  const qs = buildQueryString(search);

  const { data: completionData, isLoading: loadingCompletion } = useQuery({
    queryKey: ["reports", "completion-rate", search],
    queryFn: () =>
      apiGet<{ data: CompletionRow[] }>(`/api/reports/completion-rate?${qs}`),
  });

  const { data: reviewData, isLoading: loadingReview } = useQuery({
    queryKey: ["reports", "review-lag", search],
    queryFn: () =>
      apiGet<{ data: ReviewLagRow[]; count: number }>(
        `/api/reports/review-lag?${qs}`
      ),
  });

  const { data: unsignedData, isLoading: loadingUnsigned } = useQuery({
    queryKey: ["reports", "unsigned", search],
    queryFn: () =>
      apiGet<{
        data: {
          projectId: string;
          projectName: string;
          totalFlhas: number;
          unsignedFlhas: number;
          totalParticipants: number;
          unsignedParticipants: number;
        }[];
      }>(`/api/reports/unsigned?${qs}`),
  });

  const { data: permitData, isLoading: loadingPermits } = useQuery({
    queryKey: ["reports", "permit-compliance", search],
    queryFn: () =>
      apiGet<{ data: PermitComplianceData }>(
        `/api/reports/permit-compliance?${qs}`
      ),
  });

  const { data: hazardData } = useQuery({
    queryKey: ["reports", "hazard-frequency", search],
    queryFn: () =>
      apiGet<{ data: HazardRow[] }>(`/api/reports/hazard-frequency?${qs}`),
  });

  const { data: highRiskData } = useQuery({
    queryKey: ["reports", "high-risk-frequency", search],
    queryFn: () =>
      apiGet<{ data: HighRiskRow[] }>(
        `/api/reports/high-risk-frequency?${qs}`
      ),
  });

  const isLoading =
    loadingCompletion || loadingReview || loadingUnsigned || loadingPermits;

  // Compute stat values
  const completion = completionData?.data ?? [];
  const totalFlhas = completion.reduce((s, r) => s + r.total, 0);
  const approvedFlhas = completion.reduce((s, r) => s + r.approved, 0);
  const completionRate =
    totalFlhas > 0 ? Math.round((approvedFlhas / totalFlhas) * 100) : 0;

  const pendingReviewCount = reviewData?.count ?? 0;
  const hasLongWaiting =
    (reviewData?.data ?? []).some((r) => r.hoursWaiting > 24);

  const totalUnsigned = (unsignedData?.data ?? []).reduce(
    (s, r) => s + (r.unsignedParticipants ?? 0),
    0
  );

  const permits = permitData?.data;
  const activePermits = permits?.byStatus.active ?? 0;
  const closeOutRate = permits
    ? Math.round(permits.closeOutRate * 100)
    : 0;

  const topHazard =
    hazardData?.data && hazardData.data.length > 0
      ? hazardData.data[0]
      : null;

  const totalHighRisk = (highRiskData?.data ?? []).reduce(
    (s, r) => s + r.count,
    0
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="border rounded-lg p-3 h-20 animate-pulse bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (totalFlhas === 0 && pendingReviewCount === 0 && (permits?.total ?? 0) === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No data for the selected period.
      </p>
    );
  }

  // Bar chart data for completion by project
  const projectBars = completion.map((row) => ({
    label: row.projectName,
    value: row.approved,
    sub: `${row.total} total`,
    color:
      row.total > 0 && row.approved / row.total >= 0.8
        ? "bg-green-500"
        : row.total > 0 && row.approved / row.total >= 0.5
          ? "bg-yellow-500"
          : "bg-red-500",
  }));

  return (
    <div className="space-y-6">
      {/* KPI stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="FLHA Completion Rate"
          value={`${completionRate}%`}
          sub={`${approvedFlhas}/${totalFlhas}`}
          barPct={completionRate}
          barColor="bg-green-500"
        />
        <StatCard
          label="Pending Review"
          value={pendingReviewCount}
          className={
            hasLongWaiting
              ? "border-red-200 bg-red-50/50"
              : pendingReviewCount > 0
                ? "border-amber-200 bg-amber-50/50"
                : undefined
          }
        />
        <StatCard
          label="Unsigned FLHAs"
          value={totalUnsigned}
          className={totalUnsigned > 0 ? "border-red-200 bg-red-50/50" : undefined}
        />
        <StatCard
          label="Active Permits"
          value={activePermits}
        />
        <StatCard
          label="Permit Close-out"
          value={`${closeOutRate}%`}
          barPct={closeOutRate}
          barColor="bg-blue-500"
        />
        <StatCard
          label="Top Hazard"
          value={topHazard ? topHazard.count : 0}
          sub={topHazard?.hazardLabel ?? "N/A"}
        />
        <StatCard
          label="High-Risk Work"
          value={totalHighRisk}
          className={totalHighRisk > 0 ? "border-orange-200 bg-orange-50/50" : undefined}
        />
      </div>

      {/* Completion by Project */}
      {projectBars.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Completion by Project
          </h3>
          <BarChart items={projectBars} />
        </div>
      )}
    </div>
  );
}
